import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterSelector } from '@/core/components/FilterSelector/FilterSelector';
import { CustomSlider, type SliderRange } from '@/core/components/CustomSlider/CustomSlider';
import { SelectionSummary } from '@/core/components/SelectionSummary';
import { useSelectorItems } from '@/core/hooks/useSelectorItems';
import { CustomTooltip } from '@/core/components/CustomTooltip/CustomTooltip';
import authorService from '@/core/services/AuthorService';
import { type SelectionMap } from '@/core/components/FilterSelector/types';
import { type AuthorOption } from '@/core/services/interfaces/AuthorInterfaces';
import { AuthorSearchSelect } from './components/AuthorSearchSelect/AuthorSearchSelect';
import {
  buildFollowersPayload,
  applyMyPagesFilterToData,
  getAuthorKey,
  FOLLOWERS_MAX,
  FOLLOWERS_MIN,
  type AuthorData,
  type MyPagesFilterType
} from './AuthorsContent.defaults';
import { InterestSearchSelect } from './components/InterestSearchSelect/InterestSearchSelect';
import { InteractionsOriginSelect } from './components/InteractionsOriginSelect/InteractionsOriginSelect';
import { dispatchFilterEvent } from '../../GlobalFilterModal';
import './AuthorsContent.scss';

export const availableProfileTypesKeys = [
  'globalFilter.genderMan',
  'globalFilter.genderWom',
  'globalFilter.genderOrg',
];

interface AuthorContentProps {
  values: AuthorData;
  onChange: (data: AuthorData) => void;
  onCountChange?: (count: number) => void;
  disableFollowers?: boolean;
  disableMyPages?: boolean;
  connectedPages?: AuthorOption[];
}

export const AuthorContent: React.FC<AuthorContentProps> = ({
  values,
  onChange,
  onCountChange,
  disableFollowers = false,
  disableMyPages,
  connectedPages = [],
}) => {
  const { t } = useTranslation();
  
  const [isMyPagesOpen, setIsMyPagesOpen] = useState(false);

  const {
    authorsSelection,
    authorsMap,
    interestsSelection,
    profileTypeSelection,
    followersRange,
    profileTypeTouched = false,
    myPagesFilter = 'all',
  } = values;

  useEffect(() => {
    if (!onCountChange) return;

    // The profile type selector starts fully selected ("Todos") by default, which is visually
    // indistinguishable from an explicit "select all" - rely on profileTypeTouched to tell them
    // apart instead of only checking whether there is a non-empty selection.
    const isProfileTypeFiltered = profileTypeTouched && Object.keys(profileTypeSelection).length > 0;

    const count =
      Number(Object.keys(authorsSelection).length > 0) +
      Number(Object.keys(interestsSelection).length > 0) +
      Number(isProfileTypeFiltered) +
      Number(buildFollowersPayload(followersRange) !== undefined) +
      Number(myPagesFilter !== 'all');

    onCountChange(count);
  }, [authorsSelection, interestsSelection, profileTypeSelection, profileTypeTouched, followersRange, myPagesFilter, onCountChange]);

  const handleAuthorsChange = (updater: React.SetStateAction<SelectionMap>, authorToAdd?: AuthorOption) => {
    const nextSelection = typeof updater === 'function' ? updater(authorsSelection) : updater;

    const nextAuthorsMap = { ...authorsMap };
    const currentManualKeys = values.manualAuthorKeys ?? [];
    let nextManualKeys = currentManualKeys.filter((k) => nextSelection[k] != null);

    if (authorToAdd) {
      const key = getAuthorKey(authorToAdd);
      nextAuthorsMap[key] = authorToAdd;
      if (!nextManualKeys.includes(key)) {
        nextManualKeys = [...nextManualKeys, key];
      }
    }

    let nextMyPagesFilter = values.myPagesFilter;
    if (values.myPagesFilter !== 'all') {
      const removedSomeSyncedPage = Object.keys(authorsSelection).some(
        (key) => authorsSelection[key] != null && nextSelection[key] == null && !currentManualKeys.includes(key)
      );
      
      if (removedSomeSyncedPage) {
        nextMyPagesFilter = 'all';
        dispatchFilterEvent('AUTHOR', 'MY_PAGES_FILTER', 'all');
      }
    }

    onChange({ 
      ...values, 
      authorsSelection: nextSelection, 
      authorsMap: nextAuthorsMap, 
      manualAuthorKeys: nextManualKeys,
      myPagesFilter: nextMyPagesFilter
    });
    
    dispatchFilterEvent('AUTHOR', 'SELECTED_AUTHORS', nextSelection);
  };

  const myPagesOptions = useMemo(() => [
    { value: 'all', label: t('globalFilter.myPagesAll') },
    { value: 'myPages', label: t('globalFilter.myPagesOnlyMine') },
    { value: 'notMyPages', label: t('globalFilter.myPagesHideMine') },
  ], [t]);

  const handleMyPagesChange = (value: string) => {
    const nextFilter = value as MyPagesFilterType;
    const newData = applyMyPagesFilterToData(values, nextFilter, connectedPages);
    onChange(newData);
    dispatchFilterEvent('AUTHOR', 'MY_PAGES_FILTER', value);
  };

  const handleInterestsChange = (updater: React.SetStateAction<SelectionMap>) => {
    const nextSelection =
      typeof updater === 'function' ? updater(interestsSelection) : updater;
    onChange({ ...values, interestsSelection: nextSelection });
    dispatchFilterEvent('AUTHOR', 'INTERESTS_SELECTION', nextSelection);
  };

  const rawToTranslatedProfileTypeMap = useMemo(
    () => Object.fromEntries(
      availableProfileTypesKeys.map((key) => [key, t(key)]),
    ),
    [t],
  );

  const translatedToRawProfileTypeMap = useMemo(
    () => Object.fromEntries(
      Object.entries(rawToTranslatedProfileTypeMap).map(([rawKey, translatedLabel]) => [translatedLabel, rawKey]),
    ),
    [rawToTranslatedProfileTypeMap],
  );

  const translatedProfileTypeSelection = useMemo(
    () => Object.fromEntries(
      Object.entries(profileTypeSelection).map(([rawKey, state]) => {
        const translatedKey = rawToTranslatedProfileTypeMap[rawKey] ?? rawKey;
        return [translatedKey, state];
      }),
    ),
    [profileTypeSelection, rawToTranslatedProfileTypeMap],
  );

  const handleProfileTypeChange = (updater: React.SetStateAction<SelectionMap>) => {
    const nextTranslatedSelection =
      typeof updater === 'function' ? updater(translatedProfileTypeSelection) : updater;

    const nextRawSelection = Object.fromEntries(
      Object.entries(nextTranslatedSelection).map(([translatedKey, state]) => {
        const rawKey = translatedToRawProfileTypeMap[translatedKey] ?? translatedKey;
        return [rawKey, state];
      }),
    );

    onChange({ ...values, profileTypeSelection: nextRawSelection, profileTypeTouched: true });
    dispatchFilterEvent('AUTHOR', 'PROFILE_TYPE_SELECTION', nextRawSelection);
  };

  const handleFollowersChange = (nextRange: SliderRange) => {
    if (disableFollowers) return;
    onChange({ ...values, followersRange: nextRange });
    dispatchFilterEvent('AUTHOR', 'FOLLOWERS_RANGE', nextRange);
  };

  const { occupations } = useSelectorItems();

  const searchOccupations = useCallback(
    (term: string): Promise<string[]> => {
      const normalized = term.toLowerCase();
      return Promise.resolve(
        occupations.filter((o) => o.toLowerCase().includes(normalized)),
      );
    },
    [occupations],
  );

  const translatedProfileTypes = useMemo(() => {
    return availableProfileTypesKeys.map((key) => rawToTranslatedProfileTypeMap[key] ?? key);
  }, [rawToTranslatedProfileTypeMap]);

  const followersMarkers = useMemo(() => [
    { value: FOLLOWERS_MIN, label: '0' },
    { value: FOLLOWERS_MAX, label: t('globalFilter.unlimited') },
  ], [t]);

  const myPagesSelectComponent = (
    <InteractionsOriginSelect
      label={t('globalFilter.myPagesLabel')}
      options={myPagesOptions}
      value={myPagesFilter}
      onChange={handleMyPagesChange}
      isOpen={isMyPagesOpen}
      onOpenChange={setIsMyPagesOpen}
      disabled={disableMyPages}
      iconSize="x-small"
    />
  );

  return (
    <div className="author-content">
      <div>
        {disableMyPages ? (
          <CustomTooltip 
            content={t('globalFilter.myPagesDisabledTooltip')} 
            position="bottom" 
            className="author-tooltip-fix"
          >
            {myPagesSelectComponent}
          </CustomTooltip>
        ) : (
          myPagesSelectComponent
        )}
      </div>

      <div>
        <AuthorSearchSelect
          label={t('globalFilter.authors')}
          placeholder={t('globalFilter.placeholderAuthors')}
          helperText={t('globalFilter.authorsHelperText')}
          loadingText={t('globalFilter.loadingAuthors')}
          emptyText={t('globalFilter.emptyAuthors')}
          searchAuthors={(term) => authorService.searchAuthors(term)}
          selection={authorsSelection}
          authorsMap={authorsMap}
          onSelectionChange={handleAuthorsChange}
          allowNegation={true}
        />
        <SelectionSummary selection={authorsSelection} />
      </div>

      <div>
        <InterestSearchSelect
          label={t('globalFilter.interests')}
          placeholder={t('globalFilter.placeholderInterests')}
          loadingText={t('globalFilter.loadingInterests')}
          emptyText={t('globalFilter.emptyInterests')}
          searchFn={searchOccupations}
          selection={interestsSelection}
          onChange={handleInterestsChange}
          allowNegation={true}
        />
        <SelectionSummary selection={interestsSelection} />
      </div>

      <div>
        <FilterSelector
          label={t('globalFilter.profileType')}
          placeholder={t('globalFilter.placeholderProfileType')}
          availableItems={translatedProfileTypes}
          selection={translatedProfileTypeSelection}
          onChange={handleProfileTypeChange}
          allowNegation={false}
          isHighlighted={true}
        />
      </div>

      <div className={`author-content__slider-group${disableFollowers ? ' author-content__slider-group--disabled' : ''}`}>
        <span className="author-content__slider-label">
          {t('globalFilter.followersSubscribers')}
        </span>
        <div className="author-content__slider-wrapper">
          <CustomSlider
            min={FOLLOWERS_MIN}
            max={FOLLOWERS_MAX}
            value={followersRange}
            onChange={handleFollowersChange}
            markers={followersMarkers}
            disabled={disableFollowers}
          />
        </div>
      </div>
    </div>
  );
};
